import axios from './axiosConfig'
import { getEnv } from '@utils/env'
import { toApiError } from './errors/api-error'

const AI_BUSINESS_API = getEnv('REACT_APP_BUSINESS_LAYER_AI')

export type AiFeatureKey =
  | 'rewrite'
  | 'formalize'
  | 'summarize'
  | 'list_items'
  | 'simplify'
  | 'spelling_check'
  | 'grammar_check'
  | 'custom'

export type AiCompletionRequest = {
  clinic_id: string
  user_text: string
  feature_key?: AiFeatureKey | string
  prompt_template?: string
  system_prompt?: string
  idempotency_key?: string
  user_id: string
}

export type AiCompletionResponse = {
  statusCode: number
  message: string
  data: {
    content: string
    usage: {
      prompt_tokens: number
      completion_tokens: number
      total_tokens: number
      model: string
      latency_ms: number
      feature: string
    }
  }
}

export type AiPromptTemplate = {
  prompt_template_id: string
  feature_key: string
  template_text: string
  is_active: boolean
}

export const normalizeAiFeatureKey = (value: string): AiFeatureKey => {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, '_')
  if (
    [
      'rewrite',
      'formalize',
      'summarize',
      'list_items',
      'simplify',
      'spelling_check',
      'grammar_check',
    ].includes(normalized)
  ) {
    return normalized as AiFeatureKey
  }
  if (normalized === 'refine') return 'rewrite'
  return 'custom'
}

export const postAiCompletionApi = async (
  payload: AiCompletionRequest,
): Promise<AiCompletionResponse> => {
  try {
    const response = await axios.post<AiCompletionResponse>(
      `${AI_BUSINESS_API}/api/v1/ai/completions`,
      payload,
    )
    return response.data
  } catch (e: unknown) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const fetchAiPromptTemplatesApi = async (): Promise<{
  statusCode: number
  message: string
  data: { prompt_templates?: AiPromptTemplate[] }
}> => {
  try {
    const response = await axios.get(
      `${AI_BUSINESS_API}/api/v1/ai/prompt-templates`,
    )
    return response.data
  } catch (e: unknown) {
    throw toApiError(e, 'Failed to fetch AI prompt templates')
  }
}

export const completeAiText = async (
  clinicId: string,
  userId: string,
  userText: string,
  featureKey?: string,
): Promise<string> => {
  try {
    const response = await postAiCompletionApi({
      clinic_id: clinicId,
      user_text: userText,
      feature_key: normalizeAiFeatureKey(featureKey || 'custom'),
      user_id: userId,
    })

    if (response.statusCode === 200 && response.data?.content) {
      return response.data.content
    }

    return (
      response.message || "I'm sorry, I couldn't generate a response right now."
    )
  } catch (e: unknown) {
    return (
      toApiError(e, "I'm sorry, I couldn't generate a response right now.")
        .message || "I'm sorry, I couldn't generate a response right now."
    )
  }
}
